import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useKeenSlider } from 'keen-slider/react';
import 'keen-slider/keen-slider.min.css';

const integrityData = [
    {
        id: 1,
        title: "Make your conduct count",
        description: "Making your conduct count means treating others with respect, acting fairly, and living by shared values, so everyone feels supported to do their best every day.",
        image: "/assets/work/img4.png"
    },
    {
        id: 2,
        title: "Protect people, information and our business",
        description: "We care deeply about keeping our people, client information, and business safe, so everyone can work with confidence in a trusted environment.",
        image: "/assets/work/img5.png"
    },
    {
        id: 3,
        title: "Anti-corruption and compliance",
        description: "We do not accept or engage in any form of bribery or corruption. We stay honest in every action and follow all laws wherever we work.",
        image: "/assets/work/img4.png"
    }
];

const WorkingWithIntegritySection = () => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [loaded, setLoaded] = useState(false);

    const [sliderRef, instanceRef] = useKeenSlider({
        initial: 0,
        slideChanged(slider) {
            setCurrentSlide(slider.track.details.rel);
        },
        created() {
            setLoaded(true);
        },
        breakpoints: {
            "(min-width: 640px)": {
                slides: { perView: 1.2, spacing: 20 },
            },
            "(min-width: 1024px)": {
                slides: { perView: 2, spacing: 30 },
            },
        },
        slides: { perView: 1, spacing: 16 },
    });

    const fadeUpVariant = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
    };

    const isNextDisabled = () => {
        if (!instanceRef.current) return true;
        const totalSlides = instanceRef.current.track.details.slides.length;
        const perView = instanceRef.current.options.slides.perView;
        return currentSlide >= Math.ceil(totalSlides - perView);
    };

    return (
        <section className="w-full pb-[40px] md:pb-[100px] bg-white overflow-hidden font-sora">
            <div className="max-w-[1440px] mx-auto px-[20px] lg:px-[80px]">

                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                    variants={fadeUpVariant}
                    className="text-left max-w-[800px] mb-[32px] md:mb-[60px]"
                >
                    <h2 className="text-[20px] md:text-[30px] font-[600] text-[#01386A] tracking-tight mb-[12px] md:mb-[16px]">
                        Working with integrity
                    </h2>
                    <p className="text-[#49577A] text-[14px] md:text-[16px] font-graphik font-[500] leading-[1.6]">
                        We believe in doing the right thing, every time. Guided by strong ethics and a shared purpose, we strive to make a positive difference in everything we do, built on three simple but powerful principles.
                    </p>
                </motion.div>

                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.1 }}
                    variants={fadeUpVariant}
                    className="relative"
                >
                    <div ref={sliderRef} className="keen-slider !overflow-visible lg:!overflow-hidden">
                        {integrityData.map((item) => (
                            <div
                                key={item.id}
                                className="keen-slider__slide"
                            >
                                <div className="group flex flex-col sm:flex-row relative overflow-hidden bg-white border border-[#E4E8F5] h-full min-h-[460px] sm:min-h-0 sm:h-[350px] shadow-sm hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition-shadow duration-300 rounded-sm">
                                    <div className="w-full sm:w-1/2 overflow-hidden h-[200px] sm:h-full shrink-0">
                                        <img
                                            src={item.image}
                                            alt={item.title}
                                            className="w-full h-full object-cover transition-transform duration-700 ease-in-out group-hover:scale-105"
                                        />
                                    </div>

                                    <div className="w-full sm:w-1/2 p-[20px] sm:p-[30px] md:p-[40px] flex flex-col justify-between bg-white z-10 flex-grow">
                                        <div>
                                            <h3 className="text-[16px] md:text-[20px] font-[600] font-sora text-[#01386A] transition-transform duration-500 ease-in-out transform md:group-hover:-translate-y-2">
                                                {item.title}
                                            </h3>

                                            <div className="grid grid-rows-[1fr] md:grid-rows-[0fr] transition-[grid-template-rows] duration-500 ease-in-out group-hover:md:grid-rows-[1fr]">
                                                <div className="overflow-hidden">
                                                    <p className="text-[#49577A] text-[14px] md:text-[16px] font-graphik leading-[1.5] md:leading-[1.6] font-[500] pt-[8px] md:pt-[12px] opacity-100 md:opacity-0 transition-opacity duration-500 delay-100 group-hover:md:opacity-100">
                                                        {item.description}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="mt-auto pt-[16px] md:pt-[20px] transition-opacity duration-300 md:group-hover:opacity-0 md:group-hover:pointer-events-none">
                                            <a
                                                href="#"
                                                className="inline-flex items-center text-[14px] lg:text-[16px] font-[700] text-[#01386A] transition-colors duration-300"
                                            >
                                                <span className="border-b border-transparent">
                                                    Read More
                                                </span>
                                                <span className="ml-2 text-[#F97316] text-[16px] font-bold">&gt;</span>
                                            </a>
                                        </div>
                                    </div>

                                    <div className="absolute bottom-0 left-0 w-full h-[4px] bg-[#01386A] z-20"></div>
                                    <div className="absolute bottom-0 left-0 w-0 h-[4px] bg-[#F97316] transition-all duration-500 ease-out group-hover:w-full z-30"></div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {loaded && instanceRef.current && (
                        <div className="flex items-center gap-4 mt-6 md:mt-8">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    instanceRef.current?.prev();
                                }}
                                disabled={currentSlide === 0}
                                className={`w-10 h-10 rounded-full border flex items-center justify-center transition-colors duration-300 ${currentSlide === 0
                                    ? 'border-gray-300 text-gray-300 cursor-not-allowed'
                                    : 'border-[#F97316] text-[#F97316] hover:bg-[#F97316] hover:text-white'
                                    }`}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M19 12H5M12 19l-7-7 7-7" />
                                </svg>
                            </button>

                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    instanceRef.current?.next();
                                }}
                                disabled={isNextDisabled()}
                                className={`w-10 h-10 rounded-full border flex items-center justify-center transition-colors duration-300 ${isNextDisabled()}
                                        ? 'border-gray-300 text-gray-300 cursor-not-allowed'
                                        : 'border-[#F97316] text-[#F97316] hover:bg-[#F97316] hover:text-white'
                                    }`}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M5 12h14M12 5l7 7-7 7" />
                                </svg>
                            </button>
                        </div>
                    )}
                </motion.div>

            </div>
        </section>
    );
};

export default WorkingWithIntegritySection;